import React from 'react';

type RevealState = 'correct' | 'wrong' | null;

interface QuestionCardProps {
  company: { symbol: string; name: string; fetchDate: string };
  label: 'A' | 'B';
  onSelect: () => void;
  disabled: boolean;
  revealState: RevealState;
  revealValue: string | null;
  selected?: boolean;
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${parseInt(month)}/${parseInt(day)}/${year}`;
};

export const QuestionCard: React.FC<QuestionCardProps> = ({
  company,
  label,
  onSelect,
  disabled,
  revealState,
  revealValue,
  selected,
}) => {
  const getBorderColor = () => {
    if (revealState === 'correct') return 'var(--rush-correct)';
    if (revealState === 'wrong') return 'var(--rush-wrong)';
    if (selected) return '#3b82f6';
    return 'hsl(var(--border))';
  };

  const getBackground = () => {
    if (revealState === 'correct') return 'rgba(74, 222, 128, 0.1)';
    if (revealState === 'wrong') return 'rgba(248, 113, 113, 0.1)';
    return 'hsl(var(--card))';
  };

  const bg = getBackground();

  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className={`aspect-square w-full flex flex-col items-center justify-between text-center p-3 sm:p-5 md:p-6 transition-transform duration-200 disabled:cursor-default ${
        !disabled ? 'rush-card-hover' : ''
      } ${selected && !revealState ? 'rush-card-selected' : ''}`}
      style={{
        backgroundColor: bg,
        border: `2px solid ${getBorderColor()}`,
        transform: selected && !disabled && !revealState ? 'scale(1.1)' : undefined,
      }}
    >
      {/* Top spacer */}
      <span />

      {/* Center content */}
      <div className="flex flex-col items-center">
        <span
          className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold leading-tight"
          style={{ color: 'hsl(var(--foreground))' }}
        >
          {company.name}
        </span>
        <span
          className="text-xs sm:text-sm md:text-base mt-1"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          {company.symbol}
        </span>
        {revealValue && (
          <span
            className="text-sm sm:text-base md:text-lg font-bold mt-2 sm:mt-3"
            style={{
              color: revealState === 'correct' ? 'var(--rush-correct)' : 'var(--rush-wrong)',
            }}
          >
            {revealValue}
          </span>
        )}
      </div>

      {/* Bottom date */}
      {company.fetchDate ? (
        <span
          className="text-[10px] sm:text-xs md:text-sm"
          style={{ color: 'var(--rush-text-muted)', opacity: 0.6 }}
        >
          Data fetched {formatDate(company.fetchDate)}
        </span>
      ) : <span />}
    </button>
  );
};
